import { wafflesKeyColumnName } from "@app/shared/waffles";
import { JSX, useContext, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { authenticatedDownload } from "@app/shared/AuthenticatedDownload";
import { ITermRefinementOption, TerminationsWizardStepProps } from "@app/terminations/import/interfaces";
import { TerminationsWizardContext } from "@app/terminations/import/TerminationsWizardContext";
import DragAndDropFileUploader from "@app/shared/DragAndDropFileUploader";
import PageSection from "@app/shared/PageSection";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { QESRadioButton } from "@app/shared/qes-standards/forms/RadioButton";
import { QESRadioGroup } from "@app/shared/qes-standards/forms/RadioGroup";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import ExportService from "@app/exports/exportService";

const TerminationsWizardImportList = (props: TerminationsWizardStepProps): JSX.Element => {
  const wizardContext = useContext(TerminationsWizardContext);
  const featureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;
  const exportService = new ExportService();

  const [termType, setTermType] = useState<string>("npi");
  const [termRefinementOptions, setTermRefinementOptions] = useState<ITermRefinementOption>({ infoMessage: <></>, isAllLocations: true, isAllSpecialties: true });
  const [remainingProviderActionCount, setRemainingProviderActionCount] = useState<number>(null);
  const [bulkUploadLimit, setBulkUploadLimit] = useState<number>(0);
  const [isUploadSuccessful, setIsUploadSuccessful] = useState<boolean>(true);

  const helpFileUri = useRef<string>(`${window.apiEndpointV2_0}/providerterminations/bulkterminationsamplefile/csv`).current;

  const uploadObjectKey = "bulkTerminationFile";
  const handleFileUpload = async (data: FormData) => {
    return new Promise<void>(async (resolve, reject) => {
      try {
        const terminateByNpi = termType === "npi" || termType === "npi-tin";
        const terminateByTin = termType === "tin" || termType === "npi-tin";
        const response = await wizardContext.providerTerminationService.importBulkTerminationFile(
          data,
          terminateByNpi,
          terminateByTin,
          termRefinementOptions.isAllLocations,
          termRefinementOptions.isAllSpecialties
        );

        if (featureFlags.has(Features.BULK_UPLOAD_LIMIT) && (remainingProviderActionCount === 0 || response.uploadLimitationStatus < 0)) {
          toast(`Your Volume Action limit has been exceeded. Volume actions are limited to ${bulkUploadLimit} providers per day. Please try again later.`,
            {
              containerId: "z-index-9999",
              type: "error"
            });
          setIsUploadSuccessful(false);
          reject();
          return;
        }

        const validPlural = response.validRecordsCount !== 1;
        const nppesPlural = response.recordsInvalidByNppesCount !== 1;
        const validItems: JSX.Element = (
          <>
            <><span>{response.validRecordsCount} Valid Record{validPlural ? "s" : ""}</span><br /></>
            {response.recordsInvalidByNppesCount === 0 && <><span>All Valid NPIs are Valid NPPES NPIs</span><br /></>}
            {response.recordsInvalidByNppesCount !== 0 && <><span>{response.recordsInvalidByNppesCount} of your NPI{nppesPlural ? "s are not found in" : " is not found in"} NPPES</span><br /></>}
          </>
        )

        let invalidNpis: JSX.Element = null;
        let invalidTins: JSX.Element = null;

        if (response.invalidFormatNpis.length > 0 || response.invalidNotFoundNpis.length > 0) {
          invalidNpis = (
            <>
              {
                response.invalidFormatNpis.length > 0 &&
                <>
                  <span> The following NPIs are formatted incorrectly:<br /><br />
                    {
                      response.invalidFormatNpis.map((npi, index) => <><span key={index}>{npi}</span><br /></>)
                    }
                  </span><br />
                </>
              }
              {
                response.invalidNotFoundNpis.length > 0 &&
                <>
                  <span> The following NPI(s) are correctly formatted but invalid because they do not exist in the official registry. Please verify their accuracy or consult the source of the data.<br /><br />
                    {
                      response.invalidNotFoundNpis.map((npi, index) => <><span key={index}>{npi}</span><br /></>)
                    }
                  </span>
                </>
              }
            </>
          );
        }

        if (response.invalidFormatTins.length > 0) {
          invalidTins = (
            <>
              <span> The following TINs are formatted incorrectly:<br /><br />
                {
                  response.invalidFormatTins.map((tin, index) => <><span key={index}>{tin}</span><br /></>)
                }
              </span>
            </>
          );
        }

        setIsUploadSuccessful(true);
        setRemainingProviderActionCount(response.uploadLimitationStatus);
        wizardContext.setBulkProviderTerminationId(response.bulkProviderTerminationId);
        wizardContext.setInvalidNpis([{ invalidCount: response.invalidFormatNpis.length + response.invalidNotFoundNpis.length, invalidNpis: invalidNpis ?? <>All NPIs are valid</>, [wafflesKeyColumnName]: "key" }]);
        wizardContext.setInvalidTins([{ invalidCount: response.invalidFormatTins.length, invalidTins: invalidTins ?? <>All TINs are valid</>, [wafflesKeyColumnName]: "key" }]);
        wizardContext.setResultsSummary([{ validItems, [wafflesKeyColumnName]: "key" }]);
        wizardContext.setBulkImportFormData(data);
        wizardContext.setTerminateByNpi(terminateByNpi);
        wizardContext.setTerminateByTin(terminateByTin);
        wizardContext.setTerminateByAllLocations(termRefinementOptions.isAllLocations);
        wizardContext.setTerminateByAllSpecialties(termRefinementOptions.isAllSpecialties);
        resolve();
      } catch (error) {
        reject(error.responseJSON);
      }
    });
  };

  useEffect(() => {
    if (featureFlags.has(Features.BULK_UPLOAD_LIMIT)) {
      exportService.getVolumeActionRowCount(0).then((rowCount) => setRemainingProviderActionCount(rowCount));
      exportService.getVolumeActionUploadLimit().then((uploadLimit) => setBulkUploadLimit(uploadLimit));
    }
  }, []);

  useEffect(() => {
    props.formIsValid(!!wizardContext.bulkImportFormData && !!termType && !!wizardContext.bulkProviderTerminationId && isUploadSuccessful);
  }, [wizardContext.bulkImportFormData, termType, wizardContext.bulkProviderTerminationId, isUploadSuccessful]);

  useEffect(() => {
    if (!termRefinementOptions.isAllLocations && !termRefinementOptions.isAllSpecialties) {
      setTermRefinementOptions({
        ...termRefinementOptions,
        infoMessage:
          <>
            <div>When terminating providers:</div>
            <div>
              <ul>
                <li>Ensure&nbsp;<strong>NPI</strong>&nbsp;and/or&nbsp;<strong>TIN</strong>&nbsp;are selected as they are required identifiers.</li>
                <li>Specialty codes and address column information are required to terminate by specialty and location.&nbsp;</li>
                <ul>
                  <li>Address and Specialty fields are not required when terminating by All Locations or All Specialties, respectively.</li>
                </ul>
              </ul>
            </div>
          </>
      });
    } else if (!termRefinementOptions.isAllLocations) {
      setTermRefinementOptions({
        ...termRefinementOptions,
        infoMessage: <span>Address column information is required to terminate at specific locations.</span>
      });
    } else {
      setTermRefinementOptions({
        ...termRefinementOptions,
        infoMessage: <span>Specialty codes are required to terminate by specific specialties.</span>
      });
    }
    wizardContext.setTerminateByAllLocations(termRefinementOptions.isAllLocations);
    wizardContext.setTerminateByAllSpecialties(termRefinementOptions.isAllSpecialties);
  }, [termRefinementOptions.isAllLocations, termRefinementOptions.isAllSpecialties]);

  useEffect(() => {
    wizardContext.setTerminateByNpi(termType === "npi" || termType === "npi-tin");
    wizardContext.setTerminateByTin(termType === "tin" || termType === "npi-tin");
  }, [termType]);

  const downloadSampleFile = () => {
    authenticatedDownload(helpFileUri, undefined);
  }

  return (
    <PageSection>
      <div className="container">
        <RoleRestricted allowedRoles={[Roles.TIN_VIEWER]}>
          <fieldset>
            <div className="row">
              <div className="col">
                <p>Define what you wish to terminate on:</p>
                <QESRadioGroup
                  renderInline={true}
                >
                  <QESRadioButton
                    label="NPIs"
                    value="npi"
                    name="termination-type"
                    checked={termType === "npi"}
                    onChange={(e) => e.target.checked && setTermType((e.target as HTMLInputElement).value)}
                    testId="import-terminations__termination-type--npi"
                  />
                  <QESRadioButton
                    label="TINs"
                    value="tin"
                    name="termination-type"
                    checked={termType === "tin"}
                    onChange={(e) => e.target.checked && setTermType((e.target as HTMLInputElement).value)}
                    testId="import-terminations__termination-type--tin"
                  />
                  <QESRadioButton
                    label="Both NPIs and TINs"
                    value="npi-tin"
                    name="termination-type"
                    checked={termType === "npi-tin"}
                    onChange={(e) => e.target.checked && setTermType((e.target as HTMLInputElement).value)}
                    testId="import-terminations__termination-type--npi-tin"
                  />
                </QESRadioGroup>
              </div>
              <div className="col">
                <p>Additional Termination Refinement Options:</p>
                <div className="mt-n2">
                  <QESRadioGroup
                    renderInline={true}
                  >
                    <QESCheckbox
                      containerClassName="me-5"
                      label="All Locations"
                      checked={termRefinementOptions.isAllLocations}
                      onChange={(e) => setTermRefinementOptions({ ...termRefinementOptions, isAllLocations: e.target.checked })}
                      className="mt-0"
                      testId="import-terminations__termination-refinement-type--all-locations"
                    />
                    <QESCheckbox
                      label="All Specialties"
                      checked={termRefinementOptions.isAllSpecialties}
                      onChange={(e) => setTermRefinementOptions({ ...termRefinementOptions, isAllSpecialties: e.target.checked })}
                      className="mt-0"
                      testId="import-terminations__termination-refinement-type--all-specialties" />
                  </QESRadioGroup>
                </div>
              </div>
            </div>
          </fieldset>
        </RoleRestricted>
        {(!termRefinementOptions.isAllLocations || !termRefinementOptions.isAllSpecialties) && <QESAlert type="info" classNames="mb-2" persist={true}>{termRefinementOptions.infoMessage}</QESAlert>}
        {featureFlags.has(Features.BULK_UPLOAD_LIMIT) && remainingProviderActionCount !== null && <p className="mt-3">{`You have ${remainingProviderActionCount} provider actions left to use across Volume Targeting and Volume Terminations.`}</p>}
        <p className="mt-3">Select the files you want to upload. The data needs to be in a CSV file. Or upload a file with the <QESButton qesType="secondary" className="bg-transparent p-0" qesTestId="import-terminations__sample-file" onClick={downloadSampleFile}>correct format</QESButton>.</p>
        <DragAndDropFileUploader apiKeyName={uploadObjectKey} onUpload={handleFileUpload} filetype=".csv" hasBeenSuccessful={!!wizardContext.bulkImportFormData} testId="import-terminations__file-input" />
      </div>
    </PageSection>
  );
};

export default TerminationsWizardImportList;
